// src/private/src/pages/app/clients/StaffMeetingRequestsPage.jsx
//
// Staff-only meetings hub. Three jobs:
//   1. See every upcoming booked meeting across clients (prep view) —
//      who booked, when, and what they want to cover.
//   2. Triage meeting requests from any client — Approve (emails the
//      client a slot-picker link + opens their calendar) or Decline —
//      with a Resolved tab as the paper trail.
//   3. Open a meeting for ANY client outright (the tier/quota bypass),
//      with an option to email them the booking link.
// Grant parameters (meetings / expiry) are chosen per action via a
// popover on the button itself, so a stale global setting can't leak
// into an unrelated approval.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Popover,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import ClientDropdown from '@src/components/ClientDropdown';
import {
  approveMeetingRequest,
  declineMeetingRequest,
  fetchAllMeetingRequests,
  fetchStaffUpcomingBookings,
  issueMeetingGrant,
} from '@src/api/clientSchedule';
import { dayjs } from '@src/corelib';
import { useMessage } from '@src/context/common';
import paths from '@src/paths';


const clampInt = (v, lo, hi, dflt) => {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return dflt;
  return Math.max(lo, Math.min(hi, n));
};


function StaffMeetingRequestsPage() {
  const message = useMessage();
  const [requests, setRequests] = useState([]);
  const [resolved, setResolved] = useState(null); // null = not fetched yet
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingResolved, setLoadingResolved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('open');

  // Bypass section. Email defaults OFF — staff usually paste the link to the
  // exact person they're already talking to (no recipient guesswork).
  const [selectedClient, setSelectedClient] = useState(null); // {value,label,slug}
  const [bypassNotify, setBypassNotify] = useState(false);
  const [lastOpened, setLastOpened] = useState(null); // { name, url }

  const loadRequests = useCallback(async () => {
    try {
      const res = await fetchAllMeetingRequests();
      setRequests(res.data || []);
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Could not load requests.');
    }
  }, [message]);

  const loadResolved = useCallback(async () => {
    setLoadingResolved(true);
    try {
      const res = await fetchAllMeetingRequests({ status: 'resolved' });
      setResolved(res.data || []);
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not load resolved requests.',
      );
    } finally {
      setLoadingResolved(false);
    }
  }, [message]);

  const loadUpcoming = useCallback(async () => {
    try {
      const res = await fetchStaffUpcomingBookings({ days: 14 });
      setUpcoming(res.data || []);
    } catch (err) {
      // Soft-fail — the rest of the hub still works.
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadRequests(), loadUpcoming()]);
      setLoading(false);
    })();
  }, [loadRequests, loadUpcoming]);

  // Lazy-load the paper trail the first time the tab is opened.
  useEffect(() => {
    if (tab === 'resolved' && resolved === null) loadResolved();
  }, [tab, resolved, loadResolved]);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      message.success(
        typeof successMsg === 'function' ? successMsg(res) : successMsg,
      );
      await Promise.all([
        loadRequests(),
        loadUpcoming(),
        tab === 'resolved' || resolved !== null ? loadResolved() : null,
      ]);
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const doApprove = (id, grantParams) =>
    run(
      () => approveMeetingRequest(id, grantParams),
      'Approved — booking link emailed to the client.',
    );
  const doDecline = (id) =>
    run(() => declineMeetingRequest(id), 'Request declined.');

  const copyLink = (url) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => message.success('Link copied.'),
        () => message.error('Could not copy — select the link to copy manually.'),
      );
    } else {
      message.error('Copy not supported here — select the link to copy manually.');
    }
  };

  const doBypass = (grantParams) => {
    if (!selectedClient?.slug) {
      message.error('Pick a client first.');
      return;
    }
    const client = selectedClient;
    const url = `${window.location.origin}${paths.clientSchedule(client.slug)}`;
    run(
      () =>
        issueMeetingGrant(client.slug, {
          ...grantParams,
          notify: bypassNotify,
        }),
      (res) => {
        const emailed = res?.data?.notified_emails || [];
        return emailed.length
          ? `Meeting opened for ${client.label} — emailed ${emailed.join(', ')}.`
          : `Meeting opened for ${client.label} — copy the link below to send them.`;
      },
    ).then(() => {
      setLastOpened({ name: client.label, url });
      setSelectedClient(null);
    });
  };

  const newCount = useMemo(
    () => requests.filter((r) => r.status === 'new').length,
    [requests],
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 980, mx: 'auto' }}>
        <Skeleton variant="text" width={280} height={48} />
        <Skeleton variant="rounded" height={180} sx={{ mt: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={140} sx={{ mt: 3, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={220} sx={{ mt: 3, borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 980, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Meetings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upcoming booked meetings across all clients, the request queue, and
        the tier/quota bypass.
      </Typography>

      <UpcomingMeetingsCard bookings={upcoming} />

      {/* Open a meeting for any client (bypass) */}
      <Card sx={{ mb: 3, borderLeft: 4, borderColor: 'success.main' }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <LockOpenIcon color="success" fontSize="small" />
            <Typography variant="h6">Open a meeting for a client</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bypass tier/quota so a client can book even if they're Starter or
            out of quota — no request needed. By default we just activate it and
            give you the link to send; tick the box to auto-email their main
            contact instead.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
          >
            <Box sx={{ minWidth: 280, flex: 1 }}>
              <ClientDropdown
                label="Client"
                value={selectedClient?.value ?? ''}
                onChange={(value, option) => setSelectedClient(option)}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={bypassNotify}
                  onChange={(e) => setBypassNotify(e.target.checked)}
                />
              }
              label="Email their main contact"
            />
            <GrantActionButton
              label="Open meeting"
              confirmLabel="Open meeting"
              color="success"
              variant="contained"
              size="medium"
              icon={busy ? <CircularProgress size={16} /> : <LockOpenIcon />}
              disabled={busy || !selectedClient}
              onConfirm={doBypass}
            />
          </Stack>

          {lastOpened && (
            <Alert
              severity="success"
              sx={{ mt: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => copyLink(lastOpened.url)}
                >
                  Copy link
                </Button>
              }
            >
              Booking opened for <strong>{lastOpened.name}</strong>. Send them:{' '}
              <Box component="span" sx={{ wordBreak: 'break-all' }}>
                {lastOpened.url}
              </Box>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Requests queue: Open + Resolved paper trail */}
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">Requests</Typography>
            {newCount > 0 && (
              <Chip size="small" color="primary" label={`${newCount} new`} />
            )}
          </Stack>

          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            sx={{ mb: 2, minHeight: 36 }}
          >
            <Tab
              value="open"
              label={`Open (${requests.length})`}
              sx={{ minHeight: 36 }}
            />
            <Tab
              value="resolved"
              label={resolved === null ? 'Resolved' : `Resolved (${resolved.length})`}
              sx={{ minHeight: 36 }}
            />
          </Tabs>

          {tab === 'open' && (
            requests.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No open meeting requests right now.
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />} spacing={1.5}>
                {requests.map((req) => (
                  <RequestRow key={req.id} request={req}>
                    <Stack direction="row" spacing={1}>
                      <GrantActionButton
                        label={
                          req.status === 'email_sent'
                            ? 'Re-send link'
                            : 'Approve & send link'
                        }
                        confirmLabel={
                          req.status === 'email_sent' ? 'Re-send' : 'Approve'
                        }
                        variant="contained"
                        icon={<EventAvailableIcon />}
                        disabled={busy}
                        onConfirm={(params) => doApprove(req.id, params)}
                      />
                      <Button
                        size="small"
                        color="warning"
                        disabled={busy}
                        onClick={() => doDecline(req.id)}
                      >
                        Decline
                      </Button>
                    </Stack>
                  </RequestRow>
                ))}
              </Stack>
            )
          )}

          {tab === 'resolved' && (
            loadingResolved ? (
              <Stack spacing={1}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={48} />
                ))}
              </Stack>
            ) : (resolved || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No resolved requests yet.
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />} spacing={1.5}>
                {(resolved || []).map((req) => (
                  <RequestRow key={req.id} request={req} resolved />
                ))}
              </Stack>
            )
          )}
        </CardContent>
      </Card>
    </Box>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────

// "Today" / "Tomorrow" / "Friday, Jul 10" bucket label for a booking.
function dayLabel(startsAt) {
  const d = dayjs(startsAt);
  if (d.isSame(dayjs(), 'day')) return 'Today';
  if (d.isSame(dayjs().add(1, 'day'), 'day')) return 'Tomorrow';
  return d.format('dddd, MMM D');
}


// The prep view: every active booking across clients for the next two
// weeks, grouped by day. Before this existed, booked meetings lived
// nowhere in the SPA — staff had to dig through Google Calendar.
function UpcomingMeetingsCard({ bookings }) {
  const groups = useMemo(() => {
    const map = new Map();
    for (const b of bookings || []) {
      const label = dayLabel(b.starts_at);
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(b);
    }
    return Array.from(map.entries());
  }, [bookings]);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <CalendarMonthIcon color="primary" fontSize="small" />
          <Typography variant="h6">Upcoming meetings</Typography>
          <Chip
            size="small"
            variant="outlined"
            label={`next 14 days · ${(bookings || []).length}`}
          />
        </Stack>

        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing booked in the next two weeks.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {groups.map(([label, rows]) => (
              <Box key={label}>
                <Typography
                  variant="overline"
                  color={label === 'Today' ? 'primary' : 'text.secondary'}
                >
                  {label}
                </Typography>
                <Stack divider={<Divider flexItem />} spacing={1.25} sx={{ mt: 0.5 }}>
                  {rows.map((b) => (
                    <StaffBookingRow key={b.id} booking={b} />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}


function StaffBookingRow({ booking }) {
  const start = dayjs(booking.starts_at);
  const joinHref = booking.zoom_join_url || booking.gcal_meet_link;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ sm: 'center' }}
      justifyContent="space-between"
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {start.format('h:mm A')}
          </Typography>
          <Link
            component={RouterLink}
            to={paths.clientSchedule(booking.target_slug)}
            underline="hover"
            variant="body2"
            sx={{ fontWeight: 500 }}
          >
            {booking.target_name}
          </Link>
          {booking.is_parent && (
            <Chip size="small" variant="outlined" label="Parent" />
          )}
          {booking.tier_name && (
            <Chip size="small" variant="outlined" label={booking.tier_name} />
          )}
          <Typography variant="caption" color="text.secondary">
            {booking.duration_minutes} min
            {booking.requested_by_email ? ` · booked by ${booking.requested_by_email}` : ''}
          </Typography>
        </Stack>
        {booking.client_notes && (
          <Tooltip title={booking.client_notes} arrow placement="bottom-start">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              “{booking.client_notes}”
            </Typography>
          </Tooltip>
        )}
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        {joinHref && (
          <Button
            size="small"
            startIcon={<VideoCallIcon />}
            href={joinHref}
            target="_blank"
            rel="noreferrer"
          >
            Join
          </Button>
        )}
        {booking.gcal_html_link && (
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            href={booking.gcal_html_link}
            target="_blank"
            rel="noreferrer"
          >
            Event
          </Button>
        )}
      </Stack>
    </Stack>
  );
}


// One request row: identity + tier context + the client's own words.
// `children` slots in the action buttons (open queue) or nothing
// (resolved paper trail).
function RequestRow({ request: req, resolved = false, children }) {
  const statusColor =
    req.status === 'booking_created'
      ? 'success'
      : req.status === 'declined'
        ? 'default'
        : req.status === 'email_sent'
          ? 'info'
          : 'primary';
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ sm: 'flex-start' }}
      justifyContent="space-between"
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {req.target_slug ? (
            <Link
              component={RouterLink}
              to={paths.clientSchedule(req.target_slug)}
              underline="hover"
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              {req.target_name}
            </Link>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {req.target_name}
            </Typography>
          )}
          {req.tier_name && (
            <Chip size="small" variant="outlined" label={req.tier_name} />
          )}
          {resolved && (
            <Chip size="small" color={statusColor} label={req.status_display} />
          )}
        </Stack>
        <Typography variant="body2">{req.topic}</Typography>
        {req.body && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.25 }}
          >
            {req.body}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {dayjs(req.created_at).format('MMM D')}
          {req.requested_by_email ? ` · ${req.requested_by_email}` : ''}
          {req.requested_window ? ` · wants: ${req.requested_window}` : ''}
          {!resolved && req.status === 'email_sent' ? ' · link sent' : ''}
          {resolved && req.updated_at
            ? ` · resolved ${dayjs(req.updated_at).format('MMM D')}`
            : ''}
        </Typography>
      </Box>
      {children}
    </Stack>
  );
}


// Button whose click opens a small popover to confirm the grant
// parameters (how many meetings, how long the window stays open) right
// where the action happens — no global settings card to forget about.
function GrantActionButton({
  label,
  confirmLabel,
  icon,
  color = 'primary',
  variant = 'contained',
  size = 'small',
  disabled,
  onConfirm,
}) {
  const [anchor, setAnchor] = useState(null);
  const [meetings, setMeetings] = useState(1);
  const [days, setDays] = useState(7);

  const confirm = () => {
    setAnchor(null);
    onConfirm({
      meetings_allowed: clampInt(meetings, 1, 10, 1),
      expires_in_days: clampInt(days, 1, 90, 7),
    });
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        color={color}
        startIcon={icon}
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        {label}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {label}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
            <TextField
              label="Meetings"
              type="number"
              size="small"
              value={meetings}
              onChange={(e) => setMeetings(e.target.value)}
              inputProps={{ min: 1, max: 10 }}
              sx={{ width: 110 }}
            />
            <TextField
              label="Expires in (days)"
              type="number"
              size="small"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              inputProps={{ min: 1, max: 90 }}
              sx={{ width: 140 }}
            />
          </Stack>
          <Button fullWidth variant="contained" color={color} onClick={confirm}>
            {confirmLabel || label}
          </Button>
        </Box>
      </Popover>
    </>
  );
}


export default StaffMeetingRequestsPage;
